import { useState, useRef, useEffect } from 'react';
import { Send, Close, ExpandMore } from '@mui/icons-material';
import WebViewer from '@pdftron/pdfjs-express';
import ExpressUtils from '@pdftron/pdfjs-express-utils';
import { Page, Loader, PageTitleWithButton, Button as LinkButton } from '../../../components';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button as SMButton
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { downloadFile, base64StringToBlob, formatPascalStringToSpaces } from '../../../helpers';
import {
  getDigitalContractPackage,
  updateDigitalContract,
  createPandaDocForContractPacket,
  getDigitalContractByType,
  createPandaDocForDocument
} from '../../../fetch';
import { PandaDocInfoModal } from './panda-doc-info-modal';
import { useHistory } from 'react-router-dom';
import { IDigitalContractDocument } from '../../../models';

const useQueryParams = () => {
  return new URLSearchParams(window.location.search);
};

export const ContractMarkup = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryParams = useQueryParams();
  const buildId = queryParams.get('buildId');
  const neighborhood = queryParams.get('neighborhood');
  const buildDocumentType = queryParams.get('buildDocumentType');
  const lot = queryParams.get('lot');
  const [contractPackets, setContractPackets] = useState<IDigitalContractDocument[]>([]);
  const [isLoadingWebViewer, setIsLoadingWebView] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [openAccordion, setOpenAccordion] = useState<number | null>(null);
  const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
  const [promptSave, setPromptSave] = useState<boolean>(false);
  const [promptSaveForPandaDoc, setPromptSaveForPandaDoc] = useState<boolean>(false);
  const viewerRefs = useRef<any[]>([]);
  const pendingAccordionIndex = useRef<number | null>(null);
  const initialLoadRef = useRef<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);

  const handleOpen = () => setModalOpen(true);
  const handleClose = () => setModalOpen(false);
  const history = useHistory();

  const sendToPandaDoc = async () => {
    setIsSending(true);
    try {
      if (buildDocumentType) {
        await createPandaDocForDocument(Number(buildId), buildDocumentType);
      } else {
        await createPandaDocForContractPacket(Number(buildId));
      }

      setIsError(false);
      handleOpen();
    } catch (error) {
      setIsError(true);
      setErrorMessage(error?.Detail ?? 'Error sending document, please try again.');
      handleOpen();
      console.log(error);
    } finally {
      setIsSending(false);
    }
  };

  const fetchContractPackets = async () => {
    try {
      let data;
      if (buildDocumentType) {
        const res = await getDigitalContractByType(buildId, buildDocumentType);
        data = [res?.data];
      } else {
        const res = await getDigitalContractPackage(buildId);
        data = res?.data;
      }
      setContractPackets(data);
      setIsLoadingWebView(true);
    } catch (error) {
      if (error?.response?.status === 403) {
        history.push('/sales/contracting/purchasers');
        return;
      }
      enqueueSnackbar(error?.Detail ?? `Error loading contract packets, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    }
  };

  const handleSaveChanges = async () => {
    try {
      setIsSaving(true);
      const instance = viewerRefs.current[openAccordion]?.instance;
      if (instance) {
        const utils = new ExpressUtils();
        const { documentViewer, annotationManager } = instance.Core;

        const xfdf = await annotationManager.exportAnnotations({});
        const fileData = await documentViewer.getDocument().getFileData({});
        const resp = await utils.setFile(fileData).setXFDF(xfdf).merge();
        const blobOrArrayBuffer = await resp.getBlob();

        let blob;
        if (blobOrArrayBuffer instanceof ArrayBuffer) {
          blob = new Blob([blobOrArrayBuffer], { type: 'application/pdf' });
        } else {
          blob = blobOrArrayBuffer;
        }
        const formData = new FormData();
        formData.append('Document', blob, 'contract.pdf');

        await updateDigitalContract(
          buildId,
          contractPackets[openAccordion]?.buildDocumentType,
          formData
        );
        enqueueSnackbar(`Contract Saved!`, { variant: 'success' });
        setUnsavedChanges(false);
      }
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error saving document, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsSaving(false);
      setPromptSave(false);
      setPromptSaveForPandaDoc(false);
      setOpenAccordion(pendingAccordionIndex.current);
      pendingAccordionIndex.current = null;
    }
  };

  const handleAccordionChange = (index: number) => {
    if (unsavedChanges) {
      setPromptSave(true);
      pendingAccordionIndex.current = index;
    } else {
      setOpenAccordion(openAccordion === index ? null : index);
    }
  };

  const handleAnnotationChange = (annotations, action) => {
    if (!initialLoadRef.current) {
      if (action === 'add' || action === 'modify' || action === 'delete') {
        setUnsavedChanges(true);
      }
    }
  };

  useEffect(() => {
    if (buildId) {
      fetchContractPackets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoadingWebViewer) {
      contractPackets?.forEach((packet, index) => {
        WebViewer(
          {
            path: '/webviewer/lib',
            initialDoc: buildId ? null : '/blank.pdf',
            enabledElements: ['downloadButton']
          },
          viewerRefs.current[index]
        )
          .then(instance => {
            viewerRefs.current[index].instance = instance;
            const utils = new ExpressUtils();
            const { documentViewer, annotationManager, Tools, Annotations } = instance.Core;

            instance.UI.disableElements([
              'toolbarGroup-FillAndSign',
              'highlightToolGroupButton',
              'underlineToolGroupButton',
              'strikeoutToolGroupButton',
              'squigglyToolGroupButton',
              'stickyToolGroupButton',
              'freeTextToolButton3',
              'freeTextToolButton4',
              'shapeToolGroupButton',
              'freeHandHighlightToolGroupButton',
              'freeHandToolGroupButton',
              'freeTextToolButton-colorPalette',
              'freeTextToolButton2-opacitySlider',
              'freeTextToolButton2-colorPicker',
              'colorPickerController'
            ]);

            documentViewer.getTool('AnnotationCreateFreeText').setStyles({
              TextColor: new Annotations.Color(0, 0, 0),
              FontSize: '10pt'
            });

            documentViewer.getTool('AnnotationCreateFreeText2').setStyles({
              TextColor: new Annotations.Color(78, 125, 233),
              FontSize: '10pt'
            });

            instance.UI.setColorPalette({
              toolNames: [
                'FillAndSignCreateFreeText',
                'FillAndSignFreeText',
                Tools.ToolNames.FREETEXT
              ],
              colors: ['#000000', '#4e7de9']
            });

            // Updates for Check Stamp
            annotationManager.addEventListener('annotationChanged', (annotations, action) => {
              if (action === 'add') {
                annotations.forEach(annotation => {
                  if (annotation instanceof Annotations.StampAnnotation) {
                    annotation.setHeight(8);
                    annotation.setWidth(8);
                    annotation.adjustRect();

                    // Move the annotation 7px to the right and down
                    // We do this because of the size change above, and this adjustment keeps it centered on placement in the document
                    const rect = annotation.getRect();
                    rect.x1 += 7;
                    rect.x2 += 7;
                    rect.y1 += 7;
                    rect.y2 += 7;
                    annotation.setRect(rect);

                    annotationManager.redrawAnnotation(annotation);
                  }
                });
              }
            });

            instance.UI.setHeaderItems(function (header) {
              header.getHeader('toolbarGroup-Annotate').push({
                type: 'toolGroupButton',
                toolGroup: 'checkStampTools',
                dataElement: 'checkStampToolGroupButton',
                title: 'Check'
              });
            });

            instance.UI.setHeaderItems(header => {
              header.push({
                title: 'Download',
                type: 'actionButton',
                img: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path fill-rule="evenodd" clip-rule="evenodd" d="M8 10C8 7.79086 9.79086 6 12 6C14.2091 6 16 7.79086 16 10V11H17C18.933 11 20.5 12.567 20.5 14.5C20.5 16.433 18.933 18 17 18H16.9C16.3477 18 15.9 18.4477 15.9 19C15.9 19.5523 16.3477 20 16.9 20H17C20.0376 20 22.5 17.5376 22.5 14.5C22.5 11.7793 20.5245 9.51997 17.9296 9.07824C17.4862 6.20213 15.0003 4 12 4C8.99974 4 6.51381 6.20213 6.07036 9.07824C3.47551 9.51997 1.5 11.7793 1.5 14.5C1.5 17.5376 3.96243 20 7 20H7.1C7.65228 20 8.1 19.5523 8.1 19C8.1 18.4477 7.65228 18 7.1 18H7C5.067 18 3.5 16.433 3.5 14.5C3.5 12.567 5.067 11 7 11H8V10ZM13 11C13 10.4477 12.5523 10 12 10C11.4477 10 11 10.4477 11 11V16.5858L9.70711 15.2929C9.31658 14.9024 8.68342 14.9024 8.29289 15.2929C7.90237 15.6834 7.90237 16.3166 8.29289 16.7071L11.2929 19.7071C11.6834 20.0976 12.3166 20.0976 12.7071 19.7071L15.7071 16.7071C16.0976 16.3166 16.0976 15.6834 15.7071 15.2929C15.3166 14.9024 14.6834 14.9024 14.2929 15.2929L13 16.5858V11Z" fill="#868e96"></path> </g></svg>',
                onClick: async () => {
                  const xfdf = await annotationManager.exportAnnotations({});
                  const fileData = await documentViewer.getDocument().getFileData({});

                  // Set the annotations and document into the Utility SDK, then merge them together
                  const resp = await utils.setFile(fileData).setXFDF(xfdf).merge();

                  // Get the resulting blob from the merge operation
                  const mergedBlob = await resp.getBlob();

                  downloadFile(mergedBlob, 'myfile.pdf');
                }
              });
            });

            if (buildId) {
              instance.UI.setHeaderItems(header => {
                header.push({
                  title: 'Save',
                  type: 'actionButton',
                  img: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M0 0h24v24H0z" fill="none"/><path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"/></svg>',
                  onClick: async () => {
                    setIsSaving(true);
                    try {
                      const xfdf = await annotationManager.exportAnnotations({});
                      const fileData = await documentViewer.getDocument().getFileData({});

                      const resp = await utils.setFile(fileData).setXFDF(xfdf).merge();
                      const blobOrArrayBuffer = await resp.getBlob(); // This could return either a Blob or an ArrayBuffer

                      // Check if the response is an ArrayBuffer and convert it to a Blob if necessary
                      let blob;
                      if (blobOrArrayBuffer instanceof ArrayBuffer) {
                        blob = new Blob([blobOrArrayBuffer], { type: 'application/pdf' });
                      } else {
                        blob = blobOrArrayBuffer;
                      }
                      const formData = new FormData();
                      formData.append('Document', blob, 'contract.pdf');

                      await updateDigitalContract(buildId, packet?.buildDocumentType, formData);
                      setIsSaving(false);
                      enqueueSnackbar(`Contract Saved!`, {
                        variant: 'success'
                      });
                      setUnsavedChanges(false);
                    } catch (error) {
                      enqueueSnackbar(error?.Detail ?? `Error saving document, please try again.`, {
                        variant: 'error'
                      });
                      console.log(error);
                      setIsSaving(false);
                    }
                  }
                });
              });

              // This now lives on the page, but keeping for now because it may be needed
              // instance.UI.setHeaderItems(header => {
              //   header.push({
              //     title: 'Send to PandaDoc',
              //     type: 'actionButton',
              //     img: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M10.3009 13.6949L20.102 3.89742M10.5795 14.1355L12.8019 18.5804C13.339 19.6545 13.6075 20.1916 13.9458 20.3356C14.2394 20.4606 14.575 20.4379 14.8492 20.2747C15.1651 20.0866 15.3591 19.5183 15.7472 18.3818L19.9463 6.08434C20.2845 5.09409 20.4535 4.59896 20.3378 4.27142C20.2371 3.98648 20.013 3.76234 19.7281 3.66167C19.4005 3.54595 18.9054 3.71502 17.9151 4.05315L5.61763 8.2523C4.48114 8.64037 3.91289 8.83441 3.72478 9.15032C3.56153 9.42447 3.53891 9.76007 3.66389 10.0536C3.80791 10.3919 4.34498 10.6605 5.41912 11.1975L9.86397 13.42C10.041 13.5085 10.1295 13.5527 10.2061 13.6118C10.2742 13.6643 10.3352 13.7253 10.3876 13.7933C10.4468 13.87 10.491 13.9585 10.5795 14.1355Z" stroke="#868e96" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>',
              //     onClick: async () => {
              //       setIsSending(true);
              //       try {
              //         await createPandaDoc(buildId, docType);
              //         setIsSending(false);
              //         enqueueSnackbar(`Contract Sent!`, {
              //           variant: 'success'
              //         });
              //       } catch (error) {
              //         enqueueSnackbar(
              //           error?.Detail ?? `Error sending document, please try again.`,
              //           {
              //             variant: 'error'
              //           }
              //         );
              //         console.log(error);
              //         setIsSending(false);
              //       }
              //     }
              //   });
              // });

              annotationManager.addEventListener('annotationChanged', (annotations, action) =>
                handleAnnotationChange(annotations, action)
              );
            }

            if (packet) {
              const blob = base64StringToBlob(packet.file.fileContents);
              const url = URL.createObjectURL(blob);
              instance.UI.loadDocument(url, {
                extension: 'pdf',
                filename: `${packet.buildDocumentType}.pdf`
              });
            }
          })
          .catch(error => {
            console.error('Error initializing WebViewer:', error);
            enqueueSnackbar('Failed to load document viewer', { variant: 'error' });
          });
      });
      setIsLoadingWebView(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingWebViewer, contractPackets]);

  return (
    <Page title={'Contract Mark-up'}>
      <PageTitleWithButton
        pageTitleProps={{
          title: neighborhood && lot ? `${neighborhood} Lot ${lot}` : 'Contract Mark-up'
        }}
        button={
          <Box display="flex">
            <SMButton
              id="create-panda-doc"
              startIcon={<Send />}
              color="primary"
              variant="contained"
              onClick={() => {
                if (unsavedChanges) {
                  setPromptSaveForPandaDoc(true);
                  setPromptSave(true);
                } else {
                  sendToPandaDoc();
                }
              }}
              disabled={isSending || isSaving || contractPackets.length === 0}
            >
              Send for Signatures
            </SMButton>
          </Box>
        }
      />
      {(contractPackets.length === 0 || isLoadingWebViewer) && (
        <Box>
          <Loader position="centered" topOffset={10} type="inline" title={'Loading...'} />
        </Box>
      )}
      {contractPackets.length !== 0 && (isSaving || isSending) && (
        <Box>
          <Loader
            position="centered"
            type="fullscreen"
            title={isSaving ? 'Saving...' : 'Sending...'}
          />
        </Box>
      )}
      <Box>
        {contractPackets?.map((packet, index) => (
          <Box key={index} mb={1}>
            <Accordion
              key={index}
              expanded={openAccordion === index}
              onChange={() => {
                if (initialLoadRef.current) {
                  setUnsavedChanges(false);
                }
                initialLoadRef.current = false;
                handleAccordionChange(index);
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography>{formatPascalStringToSpaces(packet.buildDocumentType)}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box height="73vh" ref={el => (viewerRefs.current[index] = el)}></Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        ))}
        {contractPackets.length !== 0 && !isSaving && !isSending && (
          <LinkButton
            id="back-to-selection-details"
            startIcon={<Close />}
            variant="contained"
            color="inherit"
            asLink
            to={{
              pathname: `/sales/contracting/build-options/${buildId}`,
              state: { showSelectionDetails: true }
            }}
            sx={{ marginTop: '1rem' }}
          >
            Back to Selections
          </LinkButton>
        )}
      </Box>
      <Dialog
        open={promptSave}
        onClose={() => {
          setPromptSave(false);
          setUnsavedChanges(false);
          setPromptSaveForPandaDoc(false);
          setOpenAccordion(pendingAccordionIndex.current);
          pendingAccordionIndex.current = null;
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            You have unsaved changes. Do you want to save before{' '}
            {promptSaveForPandaDoc ? 'sending?' : 'switching?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <SMButton
            onClick={() => {
              if (promptSaveForPandaDoc) {
                setPromptSaveForPandaDoc(false);
                setPromptSave(false);
                setUnsavedChanges(false);
                sendToPandaDoc();
              } else {
                setPromptSave(false);
                setUnsavedChanges(false);
                setOpenAccordion(pendingAccordionIndex.current);
                pendingAccordionIndex.current = null;
              }
            }}
            color="primary"
          >
            No
          </SMButton>
          <SMButton
            onClick={async () => {
              if (promptSaveForPandaDoc) {
                await handleSaveChanges();
                sendToPandaDoc();
              } else {
                handleSaveChanges();
              }
            }}
            color="primary"
            autoFocus
          >
            Yes
          </SMButton>
        </DialogActions>
      </Dialog>
      <PandaDocInfoModal
        open={modalOpen}
        onClose={handleClose}
        buildId={buildId}
        isError={isError}
        errorMessage={errorMessage}
      />
    </Page>
  );
};
